/**
 * Modal compartido para crear / editar un usuario del comité.
 *
 * Por simplicidad de tipos, mantenemos dos forms separados (uno para
 * `create` con password obligatorio, otro para `edit` con `is_active`).
 * Las dos comparten el mismo modal y el mismo bloque de campos comunes.
 */
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Save } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { Button, useDescarteProtegido, useHayCambios } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  actualizarUsuarioEmpresa,
  crearUsuarioEmpresa,
  listarRolesEmpresa,
  type ActualizarUsuarioPayload,
  type CargoCanonico,
  type CargoSugerido,
  type CrearUsuarioPayload,
  type RolRead,
  type UsuarioRead,
} from "./api";

const cargoEnum = z.enum([
  "Administrador",
  "Operador",
  "Recaudador",
  "Técnico",
  "Administrativo",
  "Tesorero",
  "Secretario",
  "Presidente",
  "Director",
  "Otro",
]);

const createSchema = z.object({
  username: z.string().min(3, "Mínimo 3").max(64),
  nombre: z.string().min(1, "Requerido").max(120),
  email: z.string().email("Email inválido"),
  password: z.string().min(8, "Mínimo 8 caracteres").max(128),
  role: z.string().min(1, "Selecciona un rol").max(64),
  cargo: cargoEnum,
  cargo_personalizado: z.string().max(80).optional(),
});

const editSchema = z.object({
  username: z.string().min(3).max(64),
  nombre: z.string().min(1).max(120),
  email: z.string().email("Email inválido"),
  role: z.string().min(1, "Selecciona un rol").max(64),
  cargo: cargoEnum,
  cargo_personalizado: z.string().max(80).optional(),
  is_active: z.boolean(),
});

type CreateValues = z.infer<typeof createSchema>;
type EditValues = z.infer<typeof editSchema>;

const CREATE_FORM_ID = "usuario-create-form";
const EDIT_FORM_ID = "usuario-edit-form";

interface CommonProps {
  empresaId: number;
  cargosDisponibles: CargoSugerido[];
  onClose: () => void;
  onSuccess: () => void;
}

type Props =
  | ({ mode: "create" } & CommonProps)
  | ({ mode: "edit"; usuario: UsuarioRead } & CommonProps);

export function UsuarioFormModal(props: Props): JSX.Element {
  if (props.mode === "create") {
    return <CreateForm {...props} />;
  }
  return <EditForm {...props} />;
}

function CreateForm({
  empresaId,
  cargosDisponibles,
  onClose,
  onSuccess,
}: { mode: "create" } & CommonProps): JSX.Element {
  const toast = useToast();
  const roles = useRolesQuery(empresaId);
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      username: "",
      nombre: "",
      email: "",
      password: "",
      role: "funcionario",
      cargo: "Operador",
      cargo_personalizado: "",
    },
  });

  const mut = useMutation({
    mutationFn: (values: CreateValues) => {
      const payload: CrearUsuarioPayload = {
        username: values.username,
        nombre: values.nombre,
        email: values.email,
        password: values.password,
        role: values.role,
        cargo: values.cargo,
        cargo_personalizado:
          values.cargo === "Otro" ? values.cargo_personalizado || null : null,
      };
      return crearUsuarioEmpresa(empresaId, payload);
    },
    onSuccess: (u) => {
      toast.success(`Usuario ${u.nombre} creado`);
      onSuccess();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo crear"),
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  // watch() da todos los campos del formulario (react-hook-form).
  const hayCambios = useHayCambios([form.watch()]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nuevo usuario"
      size="lg"
      footer={
        <Footer formId={CREATE_FORM_ID} onClose={intentarCerrar} pending={mut.isPending}>
          {mut.isPending ? "Creando…" : "Crear usuario"}
        </Footer>
      }
    >
      <form
        id={CREATE_FORM_ID}
        onSubmit={form.handleSubmit((v) => mut.mutate(v))}
        className="space-y-4"
      >
        <CamposComunes
          form={form}
          cargos={cargosDisponibles}
          roles={roles.data ?? []}
          rolesLoading={roles.isLoading}
          usernameDisabled={false}
        />
        <PasswordField
          register={form.register("password")}
          error={form.formState.errors.password?.message}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

function EditForm({
  empresaId,
  usuario,
  cargosDisponibles,
  onClose,
  onSuccess,
}: { mode: "edit"; usuario: UsuarioRead } & CommonProps): JSX.Element {
  const toast = useToast();
  const roles = useRolesQuery(empresaId);
  const inicial = resolverCargoInicial(usuario, cargosDisponibles);

  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      username: usuario.username,
      nombre: usuario.nombre,
      email: usuario.email,
      role: usuario.role,
      cargo: inicial.cargo,
      cargo_personalizado: inicial.personalizado,
      is_active: usuario.is_active,
    },
  });

  const mut = useMutation({
    mutationFn: (values: EditValues) => {
      const payload: ActualizarUsuarioPayload = {
        nombre: values.nombre,
        email: values.email,
        role: values.role,
        cargo: values.cargo,
        cargo_personalizado:
          values.cargo === "Otro" ? values.cargo_personalizado || null : null,
        is_active: values.is_active,
      };
      return actualizarUsuarioEmpresa(empresaId, usuario.id, payload);
    },
    onSuccess: (u) => {
      toast.success(`Usuario ${u.nombre} actualizado`);
      onSuccess();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo guardar"),
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  // watch() da todos los campos del formulario (react-hook-form).
  const hayCambios = useHayCambios([form.watch()]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={`Editar usuario · ${usuario.nombre}`}
      size="lg"
      footer={
        <Footer formId={EDIT_FORM_ID} onClose={intentarCerrar} pending={mut.isPending}>
          {mut.isPending ? "Guardando…" : "Guardar cambios"}
        </Footer>
      }
    >
      <form
        id={EDIT_FORM_ID}
        onSubmit={form.handleSubmit((v) => mut.mutate(v))}
        className="space-y-4"
      >
        <CamposComunes
          form={form}
          cargos={cargosDisponibles}
          roles={roles.data ?? []}
          rolesLoading={roles.isLoading}
          usernameDisabled
        />
        <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            {...form.register("is_active")}
            className="rounded border-neutral-450 text-brand-600 focus:ring-brand-500"
          />
          <span>Usuario activo (puede iniciar sesión)</span>
        </label>
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

interface CamposCompartidos {
  username: string;
  nombre: string;
  email: string;
  role: string;
  cargo: CargoCanonico;
  cargo_personalizado?: string | undefined;
}

function useRolesQuery(empresaId: number) {
  return useQuery({
    queryKey: ["superadmin", "roles-empresa", empresaId],
    queryFn: () => listarRolesEmpresa(empresaId),
  });
}

function CamposComunes<T extends CamposCompartidos>({
  form,
  cargos,
  roles,
  rolesLoading,
  usernameDisabled,
}: {
  form: UseFormReturn<T>;
  cargos: CargoSugerido[];
  roles: RolRead[];
  rolesLoading: boolean;
  usernameDisabled: boolean;
}): JSX.Element {
  // Cast genérico a string-keys porque T es una unión incomoda de pasar a
  // register sin perder los nombres concretos.
  const reg = form.register as unknown as (
    name: keyof CamposCompartidos,
  ) => Record<string, unknown>;
  const errors = form.formState.errors as unknown as Record<
    keyof CamposCompartidos,
    { message?: string } | undefined
  >;

  const cargoActual =
    (form.watch("cargo" as never) as unknown as CargoCanonico) ?? "Operador";
  const cargoActivo = cargos.find((c) => c.valor === cargoActual);

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nombre" error={errors.nombre?.message}>
          <input
            {...reg("nombre")}
            type="text"
            className={CONTROL}
            placeholder="Pedro Pérez"
          />
        </Field>
        <Field label="Username" error={errors.username?.message}>
          <input
            {...reg("username")}
            type="text"
            disabled={usernameDisabled}
            className={`${CONTROL} disabled:bg-neutral-100 disabled:text-neutral-500`}
            placeholder="pperez"
          />
        </Field>
      </div>
      <Field label="Email" error={errors.email?.message}>
        <input
          {...reg("email")}
          type="email"
          className={CONTROL}
          placeholder="pedro@comite.cl"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Rol" error={errors.role?.message}>
          <select
            {...reg("role")}
            disabled={rolesLoading}
            className={`${CONTROL} capitalize disabled:bg-neutral-100`}
          >
            {rolesLoading && <option value="">Cargando…</option>}
            {!rolesLoading &&
              roles.map((r) => (
                <option key={r.id} value={r.code}>
                  {r.nombre} {r.empresa_id === null ? "(canónico)" : "(comité)"}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Cargo" error={errors.cargo?.message}>
          <select {...reg("cargo")} className={CONTROL}>
            {cargos.map((c) => (
              <option key={c.valor} value={c.valor}>
                {c.valor}
              </option>
            ))}
          </select>
          {cargoActivo && (
            <span className="block text-xs text-neutral-500 mt-1">
              {cargoActivo.descripcion}
            </span>
          )}
        </Field>
      </div>
      {cargoActual === "Otro" && (
        <Field
          label="Cargo personalizado"
          error={errors.cargo_personalizado?.message}
        >
          <input
            {...reg("cargo_personalizado")}
            type="text"
            className={CONTROL}
            placeholder="Ej: Inspector zonal"
          />
        </Field>
      )}
    </>
  );
}

const CONTROL =
  "block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm " +
  "shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

function PasswordField({
  register,
  error,
}: {
  register: ReturnType<UseFormReturn<CreateValues>["register"]>;
  error?: string | undefined;
}): JSX.Element {
  return (
    <Field label="Password inicial" error={error}>
      <input
        {...register}
        type="text"
        className={`${CONTROL} font-mono`}
        placeholder="mín. 8 caracteres"
      />
    </Field>
  );
}

function Footer({
  formId,
  onClose,
  pending,
  children,
}: {
  formId: string;
  onClose: () => void;
  pending: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      <Button variant="secondary" onClick={onClose} disabled={pending}>
        Cancelar
      </Button>
      <Button type="submit" form={formId} icon={Save} disabled={pending}>
        {children}
      </Button>
    </>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="block font-medium text-neutral-700 mb-1">{label}</span>
      {children}
      {error && (
        <span className="block text-xs text-rose-700 mt-1">{error}</span>
      )}
    </label>
  );
}

function resolverCargoInicial(
  usuario: UsuarioRead,
  disponibles: CargoSugerido[],
): { cargo: CargoCanonico; personalizado: string } {
  if (!usuario.cargo) {
    return { cargo: "Operador", personalizado: "" };
  }
  const match = disponibles.find((c) => c.valor === usuario.cargo);
  if (match) {
    return { cargo: match.valor, personalizado: "" };
  }
  return { cargo: "Otro", personalizado: usuario.cargo };
}
